// University of Connecticut: public college page (#2839), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - UConn's Common Data Set 2025-2026 (Budget, Planning and Institutional
//    Research; the PDF sits on bpir.media.uconn.edu and the source link is
//    the bpir.uconn.edu page that lists every edition, 2025-26 the newest):
//    C1 applicants, admits and enrollees for Fall 2025 with the residency
//    table (the Unknown column is blank and left out), C8 test policy for
//    Fall 2027, C21/C22 with early decision counts for the Fall 2025
//    entering class (C22: no early action plan). The CDS gives one count for
//    the university with no split by campus; UConn's first-year page puts
//    applicants "across all campuses" at 59,000+, which matches it.
//  - admissions.uconn.edu: First-Year Dates & Deadlines ("Applying for Fall
//    2027"), Early Decision Applicants (the binding agreement), First-Year
//    Application Instructions (test optional, Fine Arts and Nursing
//    components), First-Year Admission Requirements, Special Programs,
//    Decisions (first- and second-choice campus).
//  - Office of Student Financial Aid Services, 2026-2027 Direct Costs and
//    Cost of Attendance: the Storrs on-campus cost of attendance, item by
//    item. Departure: the CDS G1/G5 lists the same tuition and fees but a
//    lower housing figure ($8,288, the standard double rate, where the
//    aid office uses the $10,186 average of all Storrs housing), other
//    expenses of $2,060 and no loan fees; the aid office's budget is used
//    because it is the one UConn builds aid offers on, and its items sum to
//    its own totals.
//  - College Scorecard API, school id 129020 (net price by family income,
//    in-state).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UConn Common Data Set 2025-2026',
        url: 'https://bpir.uconn.edu/home/institutional-research/data-resources/common-data-set/',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'UConn Undergraduate Admissions, First-Year Dates & Deadlines',
        url: 'https://admissions.uconn.edu/apply/first-year/deadlines/',
        read: '2026-10-01',
    },
    earlyDecision: {
        label: 'UConn Undergraduate Admissions, Early Decision Applicants',
        url: 'https://admissions.uconn.edu/apply/early-decision/',
        read: '2026-10-01',
    },
    instructions: {
        label: 'UConn Undergraduate Admissions, First-Year Application Instructions',
        url: 'https://admissions.uconn.edu/apply/first-year/instructions/',
        read: '2026-10-01',
    },
    requirements: {
        label: 'UConn Undergraduate Admissions, First-Year Admission Requirements',
        url: 'https://admissions.uconn.edu/apply/first-year/requirements/',
        read: '2026-10-01',
    },
    specialPrograms: {
        label: 'UConn Undergraduate Admissions, Special Programs',
        url: 'https://admissions.uconn.edu/apply/first-year/special-programs/',
        read: '2026-10-01',
    },
    coa: {
        label: 'UConn Office of Student Financial Aid Services, 2026-2027 Direct Costs and Cost of Attendance',
        url: 'https://financialaid.uconn.edu/cost/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?129020-University-of-Connecticut',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-connecticut',
    kbId: 'university_of_connecticut',
    name: 'University of Connecticut',
    shortName: 'UConn',
    aliases: ['UConn'],
    location: 'Storrs, CT',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (UConn brand guidelines); decoration only.
    branding: {
        primary: '#000E2F',
        secondary: '#7C878E',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_connecticut_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 59553,
        admitted: 32369,
        enrolled: 4663,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 15633, admitted: 6337 },
            { group: 'Out-of-state', applied: 38178, admitted: 22349 },
            { group: 'International', applied: 5742, admitted: 3683 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UConn\'s Common Data Set reports one first-year acceptance rate for the university, with no rate by campus, school or major. The count covers Storrs and the four regional campuses together.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision',
        note: 'UConn has no early action plan. Its December 1 date is a priority deadline for merit scholarship and Honors consideration, not an early round.',
        year: 'Fall 2025',
        rows: [
            { plan: 'Early Decision', applied: 1442, admitted: 672 },
        ],
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You pick a first- and second-choice campus when you apply. A few programs ask for more:',
        rows: [
            { school: 'School of Fine Arts', requirement: 'Acting and Design and Technical Theatre send audition or portfolio materials through SlideRoom by January 1; Puppet Arts, Jazz Studies and Music need an audition scheduled by January 1; Art and the Digital Media Design BFA need a portfolio through SlideRoom. Early Decision applicants send these by November 1. Art History, Theatre Studies and the Digital Media Design BA need nothing extra.', source: 'instructions' },
            { school: 'Elisabeth DeLuca School of Nursing', requirement: 'An extra essay question on what has shaped your interest in nursing, answered in the application.', source: 'instructions' },
            { school: 'College of Engineering and School of Nursing', requirement: 'Engineering applicants need high school chemistry or physics; Nursing applicants need high school chemistry and biology.', source: 'requirements' },
            { school: 'Special Programs in Medicine, Dental Medicine and Law', requirement: 'Apply by November 1 and send a supplemental application by November 8; you can apply to one Special Program. Decisions come in early March. You do not need to apply Early Decision.', source: 'specialPrograms' },
        ],
        source: 'instructions',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Storrs campus deadline', value: 'January 15' },
        rows: [
            { plan: 'Early Decision', deadline: 'November 1', decision: 'Notifications begin mid-December', binding: true },
            { plan: 'Priority deadline (merit and Honors)', deadline: 'December 1', decision: 'Notifications begin early March', binding: false },
            { plan: 'Storrs campus deadline', deadline: 'January 15', decision: 'Notifications begin early March', binding: false },
            { plan: 'Regional campus deadline', deadline: 'May 1', decision: 'Rolling', binding: false },
        ],
        note: 'Early Decision applicants send their STARS Record (courses and grades entered by the student) by November 8, and a FAFSA by November 1 to get an aid offer with the decision. The FAFSA deadline for everyone else is February 15. Connecticut residents, or students at a Connecticut high school, who apply between October 15 and November 2, 2026 pay no application fee. Regular Decision admits have until May 1 to pay the enrollment fee.',
        source: 'deadlines',
        binding: 'Early Decision is binding: if admitted, you agree to enroll at UConn and withdraw your other applications. You are released if you are not admitted to your first-choice campus or major, or if you are deferred to the regular cycle.',
        bindingSource: 'earlyDecision',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'UConn lets first-year applicants choose whether to send SAT or ACT scores, and says this holds for the foreseeable future. If you want scores considered, they must be official scores sent by the testing agency. UConn superscores the SAT and ACT, and recalculates the ACT composite without science; writing and science sections are not used.',
        source: 'instructions',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required but considered if submitted for fall 2027 applicants, with scores due by January 15.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [17010, 39678] },
            { item: 'University and student fees', amounts: [4564, 4564] },
            { item: 'Housing (on campus)', amounts: [10186, 10186] },
            { item: 'Food', amounts: [6896, 6896] },
            { item: 'Books, course materials, supplies and equipment', amounts: [1020, 1020] },
            { item: 'Transportation', amounts: [2494, 2704] },
            { item: 'Personal expenses', amounts: [1968, 1968] },
            { item: 'Loan fees', amounts: [92, 92] },
        ],
        totals: [44230, 67108],
        note: 'For an undergraduate at the Storrs campus living on campus, fall and spring. Students from other New England states in the Regional Student Program pay $26,028 in tuition, a $53,458 total. Health insurance ($3,214, the 2025-2026 rate) is billed unless you waive it with your own coverage, and is not in these totals.',
        source: 'coa',
        netPrice: {
            average: 24342,
            byIncome: [
                { income: '$0 to $30,000', amount: 13649 },
                { income: '$30,001 to $48,000', amount: 13415 },
                { income: '$48,001 to $75,000', amount: 17169 },
                { income: '$75,001 to $110,000', amount: 26207 },
                { income: 'Over $110,000', amount: 36552 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UConn\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
